import React__default from 'react';
import styled from 'styled-components';
import Box from '../../components/Box/Box.js';
import Text from '../../components/Text/Text.js';
import ScrollArea from '../../components/ScrollArea/ScrollArea.js';
import { get } from '../../constants.js';

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
// List
// --------------------------------------------
const ScrollableList = ({
  children,
  spacing = 'none',
  height = 408,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(StyledScrollArea, {
    height: height,
    tabIndex: 0
  }, /*#__PURE__*/React__default.createElement(StyledScrollAreaViewport, {
    spacing: spacing
  }, children), /*#__PURE__*/React__default.createElement(StyledScrollAreaScrollbar, {
    orientation: "vertical"
  }, /*#__PURE__*/React__default.createElement(StyledScrollAreaThumb, null)), /*#__PURE__*/React__default.createElement(StyledScrollAreaScrollbar, {
    orientation: "horizontal"
  }, /*#__PURE__*/React__default.createElement(StyledScrollAreaThumb, null)), /*#__PURE__*/React__default.createElement(StyledScrollArea.Corner, null));
};
ScrollableList.displayName = "ScrollableList"; // Item
// --------------------------------------------
const Item = ({
  UNSAFE_className,
  leadingVisual: LeadingVisual,
  children,
  label,
  description,
  appearance,
  onSelect,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(ItemWrapper, _extends({
    appearance: appearance,
    className: UNSAFE_className
  }, props), /*#__PURE__*/React__default.createElement(InnerVisualSlot, null, typeof LeadingVisual === 'function' ? /*#__PURE__*/React__default.createElement(LeadingVisual, null) : LeadingVisual), children, label || description ? /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      display: 'flex',
      flexDirection: 'column',
      gap: 1
    }
  }, label && /*#__PURE__*/React__default.createElement(Text, {
    sx: {
      fontWeight: 'medium'
    }
  }, label), description && /*#__PURE__*/React__default.createElement(Text, {
    sx: {
      color: 'text.subtle'
    }
  }, description)) : null);
};
Item.displayName = "Item"; // InnerVisualSlot
/** Inner visual slot to house leading visual, trailing visual, counter, and loading spinners */
const InnerVisualSlot = ({
  children
}) => {
  // just render a visual
  return /*#__PURE__*/React__default.createElement("span", {
    className: "scrollable-list-item-visual"
  }, /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      display: 'flex',
      alignItems: 'center',
      gap: 2
    }
  }, children));
};
InnerVisualSlot.displayName = "InnerVisualSlot";
const StyledScrollArea = styled(ScrollArea).withConfig({
  displayName: "ScrollableList__StyledScrollArea",
  componentId: "sc-1c90my0-0"
})(["height:", ";overflow:hidden;background-color:", ";--scrollbar-size:10px;"], p => p.height ? `${p.height}px` : '408px', get('colors.elevation.surface.default.[default]'));
const StyledScrollAreaViewport = styled(ScrollArea.Viewport).withConfig({
  displayName: "ScrollableList__StyledScrollAreaViewport",
  componentId: "sc-1c90my0-1"
})(["width:100%;height:100%;border-radius:inherit;padding-right:", ";padding-left:", ";padding-top:", ";padding-bottom:", ";"], p => p.spacing === 'default' ? get('space.3') : p.spacing === 'compact' ? get('space.1') : p.spacing === 'none' ? 0 : p.spacing === 'relaxed' ? get('space.4') : get('space.2'), p => p.spacing === 'default' ? get('space.3') : p.spacing === 'compact' ? get('space.1') : p.spacing === 'none' ? 0 : p.spacing === 'relaxed' ? get('space.4') : get('space.2'), p => p.spacing === 'default' ? get('space.4') : p.spacing === 'compact' ? get('space.2') : p.spacing === 'none' ? 0 : p.spacing === 'relaxed' ? get('space.5') : get('space.3'), get('space.4'));
const StyledScrollAreaScrollbar = styled(ScrollArea.Scrollbar).withConfig({
  displayName: "ScrollableList__StyledScrollAreaScrollbar",
  componentId: "sc-1c90my0-2"
})(["display:flex;user-select:none;touch-action:none;padding:2px;background-color:", ";transition:background-color 160ms ease-out;&:hover{background-color:", ";}&[data-orientation='vertical']{width:var(--scrollbar-size);}&[data-orientation='horizontal']{flex-direction:column;height:var(--scrollbar-size);}"], get('colors.background.neutral.subtle.default'), get('colors.background.neutral.subtle.hovered'));
const StyledScrollAreaThumb = styled(ScrollArea.Thumb).withConfig({
  displayName: "ScrollableList__StyledScrollAreaThumb",
  componentId: "sc-1c90my0-3"
})(["flex:1;background-color:", ";border-radius:var(--scrollbar-size);position:relative;&::before{content:'';position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:100%;height:100%;min-width:44px;min-height:44px;}"], get('colors.background.neutral.default.pressed'));
const ItemWrapper = styled(Box).withConfig({
  displayName: "ScrollableList__ItemWrapper",
  componentId: "sc-1c90my0-4"
})(["padding:", " 0;border-radius:", ";outline:none;cursor:default;color:", ";font-size:", ";position:relative;width:100%;padding:6px 12px 6px 16px;display:flex;align-items:center;gap:", ";&:focus{background:", ";color:", ";}&:hover{background:", ";color:", ";}&[data-focus-visible]{background:", ";color:", ";}&[aria-selected='true']{background-color:", ";color:", ";font-weight:600;box-shadow:inset 2px 0px 0px ", ";&:hover{background:", ";color:", ";}}&[data-pressed]{background-color:", ";color:", ";}&[aria-disabled]{background-color:", ";color:", ";cursor:not-allowed;}@media (forced-colors:active){&:focus{background:Highlight;color:HighlightText;}}"], get('space.2'), p => p.appearance === 'rule-item' ? get('radii.2') : '', get('colors.text.default'), get('fontSizes.3'), get('space.2'), get('colors.background.neutral.subtle.hovered'), get('colors.text.default'), get('colors.background.neutral.subtle.hovered'), get('colors.text.default'), get('colors.background.neutral.subtle.hovered'), get('colors.text.default'), get('colors.background.selected.default.hovered'), get('colors.text.selected'), get('colors.border.selected'), get('colors.background.neutral.subtle.hovered'), get('colors.text.default'), get('colors.background.neutral.subtle.pressed'), get('colors.text.default'), get('colors.elevation.surface.default.[default]'), get('colors.text.disabled'));
// Exports
Item.displayName = 'Item';
ScrollableList.displayName = 'ScrollableList';
var ScrollableList$1 = Object.assign(ScrollableList, {
  Item
});

export { InnerVisualSlot, ItemWrapper, StyledScrollArea, StyledScrollAreaScrollbar, StyledScrollAreaThumb, StyledScrollAreaViewport, ScrollableList$1 as default };
